<template>
  <div v-if="isPresetsEnabled" class="preferences">
    <div class="header-splitter"></div>

    <input-message-box
      v-if="!presetsValue && $route.path !== '/home'"
      @data-received="saveCurrentParameters"
      :modal-title="'Save current filter settings as a preset'"
      text-field-name="Preset Name"
      :text-field-value="defaultPresetLabel"
      :disabled="apiRequestIsPerforming">

      <template #action-button="buttonProps">
        <b-button :disabled="apiRequestIsPerforming"
                  @click="buttonProps.showModal"
                  variant="link"
                  class="preference-button save-button">
          <i title="Save the current parameters as a preset"
             class="fa fa-lg fa-save"></i>
        </b-button>
      </template>
    </input-message-box>

    <b-form-group class="preferences-multiselect">
      <multiselect
        @input="applyPreset"
        v-model="presets.value"
        :options="presetsOptions"
        group-values="presets"
        group-label="section"
        :group-select="false"
        :disabled="apiRequestIsPerforming"
        :selectLabel="''"
        :deselectLabel="''"
        :multiple="false"
        :close-on-select="true"
        :limit="2"
        :searchable="true"
        label="text"
        selectedLabel=""
        track-by="text"
        placeholder="Preferences">

        <template #option="props">
          <div v-if="props.option.$isLabel" class="text">
            <strong>{{ props.option.$groupLabel }}</strong>
          </div>
          <div v-else class="pl-3">
            {{ props.option.text }}
          </div>
        </template>

        <template #noResult>
          <div>No elements found</div>
        </template>

      </multiselect>
    </b-form-group>

    <filter-presets-modal
      :modal-title="'Presets'">
      <template #open-button="buttonProps">
        <b-button :disabled="apiRequestIsPerforming"
                  @click="buttonProps.showModal"
                  variant="link"
                  class="preference-button save-button">
          <i title="Filter presets management"
             class="fa fa-lg fa-list-alt"></i>
        </b-button>
      </template>
    </filter-presets-modal>

    <div class="header-splitter"></div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { mapActions, mapMutations, mapGetters } from 'vuex'
import debounce from 'lodash-es/debounce'
import startCase from 'lodash-es/startCase'
import sortBy from 'lodash-es/sortBy'
import moment from 'moment'
import InputMessageBox from './InputMessageBox'
import FilterPresetsModal from './FilterPresetsModal'


// settings
const debounceDelay = 50;
const enabledPaths = [
  '/home',
  '/tradeflows',
  '/port-tradeflows',
  '/ports-comparison',
  '/live-stats',
  '/live-map',
  '/vessel-details',
  '/fleet-monitor',
  '/tonnage-zone-map',
  '/tonnage-basins',
  '/tonnage-zones',
  '/tonnage-speed',
  '/fleet-growth',
  '/drydock',
  '/drydock-summary',
  '/congestion',
  '/congestion-old', // todo: remove when congestion is ready
  '/congestion-summary',
];


export default {
  name: 'filter-presets',

  components: {
    FilterPresetsModal,
    InputMessageBox,
    Multiselect,
  },

  data() {
    return {
      presets: {
        // choices is a computed value: this.presetsOptions
        value: null,
      },

      apiRequestIsPerforming: false,
      keepPresetsSelector: false,
    };
  },

  async created() {
    await this.loadAllPresets();
  },

  methods: {
    ...mapMutations('api/presets', [
      'setActivePreset',
    ]),

    ...mapMutations('api/presets', [
      'updateIsPresetMatchFilters',
      'setStateValue',
    ]),

    ...mapActions('api/presets', [
      'loadAllPresets',
      'saveCurrentFilterSettings',
      'removePreset',
    ]),

    applyPreset() {
      if(this.currentPreset && this.currentPreset.viewName !== this.$route.path) {
        this.setActivePreset(this.presetsValue);

        this.setStateValue({name: 'lockPresetsSelector', value: true});
        this.$router.push(this.currentPreset.viewName).then(() => {
          this.setStateValue({name: 'lockPresetsSelector', value: false});
        });
      } else {
        this.setActivePreset(this.presetsValue);
      }
    },

    async saveCurrentParameters(data) {
      let result = await this.saveCurrentFilterSettings({
        path: this.$route.path,
        label: data['Preset Name'],
      });

      if(result && result.viewId) {
        this.presets.value = {
          value: result.viewId,
          text: result.label,
        };
        this.applyPreset();
        // it's needed when a new preset is created, but there were no user interactions,
        // "isPresetMatchFilters" is false this case, so it's needed to force update it
        this.updateIsPresetMatchFilters(this.$route.path);
      }
    },
  },

  computed: {
    ...mapGetters(
      'api', [
        'getActiveRequestsCount',
      ]
    ),
    ...mapGetters(
      'api/presets', [
        'getAllPresets',
        'getActivePreset',
        'isPresetMatchFilters',
        'getLockPresetsSelector',
      ]
    ),

    presetsOptions() {
      const sortedPresets = sortBy(
        this.getAllPresets, ['viewName', 'label']
      );

      let options = [];
      let currentGroup = {};

      sortedPresets.forEach(preset => {
        let presetChoice = {
          value: preset.viewId,
          text: `${preset.label}`
        };

        if(currentGroup.section !== startCase(preset.viewName)) {
          currentGroup = {
            section: startCase(preset.viewName),
            presets: [presetChoice],
          }
          options.push(currentGroup);
        } else {
          currentGroup.presets.push(presetChoice);
        }
      });

      return options;
    },

    presetsValue() {
      if(this.presets.value) {
        return this.presets.value.value;
      } else {
        return null;
      }
    },

    isPresetsEnabled() {
      return enabledPaths.includes(this.$route.path);
    },

    currentPreset() {
      if(this.currentPresetId) {
        return this.getAllPresets.find(item => item.viewId === this.currentPresetId) || null;
      } else {
        return null;
      }
    },

    currentPresetId() {
      if(this.presets.value) {
        return this.presetsValue;
      } else {
        return null;
      }
    },

    defaultPresetLabel() {
      return `${this.$route.name} - ${moment().format('YYYY-MM-DD HH:mm:ss')}`
    },
  },

  watch: {
    getActiveRequestsCount: debounce(function (newValue) {
      this.apiRequestIsPerforming = !!newValue;
    }, debounceDelay),

    getActivePreset: function (preset) {
      // console.log('[filter-presets] watch getActivePreset', preset ? preset.label : preset);

      if(preset) {
        this.presets.value = {text: preset.label, value: preset.viewId};
      }
    },

    isPresetMatchFilters: function (value) {
      if(!value) {
        // console.log(
        //   '[filter-presets] reference doesn\'t match current filters, "applyPreset" with empty preset',
        //   !this.getLockPresetsSelector
        // );

        if(!this.getLockPresetsSelector) {
          this.presets.value = null;
          this.applyPreset();  // apply empty preset
        }
      }
    },

    '$route.path': function () {
      // console.log('[filter-presets] watch $route.path, lock selector:', this.getLockPresetsSelector);

      if(!this.getLockPresetsSelector) {
        // console.log('[filter-presets] reset the current preset');
        this.presets.value = null;
        this.applyPreset();  // apply empty preset
      }
    },
  },
}
</script>

<style scoped>
.preferences >>> .multiselect--active {
  min-width: 350px;
}
</style>

<style scoped lang="scss">
.preferences {
  display: flex;
  justify-content: flex-end;
  align-items: center;

  .preference-button {
    padding: 0 0.6rem;
    color: #8e8e8e;
    cursor: pointer;
  }

  .save-button:hover {
    color: #3bc2e3;
  }

  .preferences-multiselect {
    margin: 0.5rem;

    &__list-caption {
      padding: 0.2rem;
      background-color: #1fabd9;
      font-size: 18px;
      text-align: center;
      color: white;
    }
  }
}

.header-splitter {
  height: 25px;
  width: 1px;
  background-color: rgba(82, 82, 82, 0.72);
  margin: 0 0.75rem;
}
</style>
